/**
 * Tenant + company scope-ыг URL-аас уншиж react context, api/client.ts-руу
 * sync хийдэг provider. CLAUDE.md §4.0-ийн дагуу URL = source of truth.
 *
 *   /:tenantId/:companyId/<page>
 *
 * Provider responsibilities:
 *   1. URL params-ийг (Guid) шалгана. Буруу бол `/select-scope` руу redirect.
 *   2. `/api/tenant/my` + `/api/company/by-tenant/{tid}` query-ээр membership шалгана.
 *      Хэрэв tenantId/companyId нь хэрэглэгчийн жагсаалтанд алга бол `forbidden`.
 *   3. `setApiActiveScope({...})` дуудаж бүх API request-д header inject.
 *   4. `tsetsu.tenantId/Name`, `tsetsu.companyId/Name` localStorage-ийг "last
 *      visited cache" болгож шинэчилнэ (legacy redirect-д хэрэглэнэ).
 *   5. `useScopedNavigate()` hook-аар scoped path-руу шилжих helper.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listMyTenants, toRoleInt } from '@/shared/api/services/tenant';
import { listCompaniesByTenant } from '@/shared/api/services/company';
import { setApiActiveScope } from '@/shared/api/client';
import { clearCashierSession } from '@/shared/api/services/pos';
import {
  buildScopedPath,
  isLikelyGuid,
  publicRoutes,
  type RouteScope,
  type ScopedRouteKey,
} from '@/app/routes';
import { setAuth } from '@/shared/auth/storage';

type ScopeStatus = 'pending' | 'ok' | 'forbidden' | 'unknown';

interface TenantScopeValue extends RouteScope {
  tenantName: string;
  companyName: string;
  /**
   * Идэвхтэй company-ийн BusinessType. Backend default-аар int буцаадаг ч
   * JsonStringEnumConverter-аар "RetailStore" гэх мэт string ирэх боломжтой.
   * UI consumer-ууд `businessTypeMeta(value)`-аар normalize хийнэ. null = pending.
   */
  companyBusinessType: number | string | null;
  status: ScopeStatus;
  /**
   * Тухайн tenant дахь идэвхтэй хэрэглэгчийн role (`MyRole`).
   * 0 = Owner, 1 = Admin, 2 = Member, 99 = unknown.
   * Closed PO correction гэх мэт role-gated UI-д permission gate болно.
   */
  myRole: number;
  /** Scope солих — URL-руу navigate, page reload хийхгүй. */
  switchTo: (
    next: RouteScope,
    opts?: { route?: ScopedRouteKey; replace?: boolean },
  ) => void;
  /** Membership query-ийг refetch. */
  refresh: () => void;
}

const TenantScopeContext = createContext<TenantScopeValue | null>(null);

export function useTenantScope(): TenantScopeValue {
  const ctx = useContext(TenantScopeContext);
  if (!ctx) {
    throw new Error(
      'useTenantScope must be used inside <TenantScopeProvider> (a /:tenantId/:companyId route).',
    );
  }
  return ctx;
}

/** `(routeKey, query?) => void` буцаана; idiomatic Link-ээр оронд `buildScopedPath`. */
export function useScopedNavigate(): (
  routeKey: ScopedRouteKey,
  query?: Record<string, string | number | null | undefined>,
  opts?: { replace?: boolean },
) => void {
  const scope = useTenantScope();
  const navigate = useNavigate();
  return useCallback(
    (routeKey, query, opts) => {
      navigate(buildScopedPath(routeKey, scope, query), {
        replace: opts?.replace ?? false,
      });
    },
    [navigate, scope],
  );
}

export function TenantScopeProvider({ children }: { children: ReactNode }) {
  const params = useParams<{ tenantId: string; companyId: string }>();
  const navigate = useNavigate();
  const loc = useLocation();
  const queryClient = useQueryClient();

  const tenantId = params.tenantId ?? '';
  const companyId = params.companyId ?? '';
  const guidsValid = isLikelyGuid(tenantId) && isLikelyGuid(companyId);

  const tenantsQ = useQuery({
    queryKey: ['scope', 'tenants'],
    queryFn: listMyTenants,
    enabled: guidsValid,
    staleTime: 60_000,
  });

  const companiesQ = useQuery({
    queryKey: ['scope', 'companies', tenantId],
    queryFn: () => listCompaniesByTenant(tenantId),
    enabled: guidsValid && !!tenantId,
    staleTime: 60_000,
  });

  const tenantMatch = useMemo(
    () => tenantsQ.data?.find((t) => t.Id === tenantId) ?? null,
    [tenantsQ.data, tenantId],
  );
  const companyMatch = useMemo(
    () => companiesQ.data?.find((c) => c.Id === companyId) ?? null,
    [companiesQ.data, companyId],
  );

  let status: ScopeStatus = 'pending';
  if (!guidsValid) status = 'forbidden';
  else if (tenantsQ.isError || companiesQ.isError) status = 'unknown';
  else if (tenantsQ.data && companiesQ.data) {
    status = tenantMatch && companyMatch ? 'ok' : 'forbidden';
  }

  // API client scope-ыг **render-ийн дотор** sync хийнэ. useEffect нь child
  // component-уудын query effect-үүдээс хойш (parent effects ажилладаг last
  // бөгөөд children first) ажиллах тул race condition үүсгэдэг — шинэ scope
  // URL-руу navigate хийсний дараа child queries хуучин X-Ts-Tenant/Company
  // header-аар явж, хуучин tenant-ийн өгөгдлийг шинэ scope-ийн query cache-д
  // бичнэ. Render-д idempotent guard-аар тогтоох нь child effect-үүдээс
  // өмнө гарцаагүй биелэгдэнэ. localStorage cache (Name-уудтай) нь header
  // үүсгэхэд primary эх биш тул effect-д үлдээнэ.
  const lastSyncedKeyRef = useRef<string | null>(null);
  if (status === 'ok' && tenantMatch && companyMatch) {
    const key = tenantId + ':' + companyId;
    if (lastSyncedKeyRef.current !== key) {
      lastSyncedKeyRef.current = key;
      setApiActiveScope({ tenantId, companyId });
    }
  }

  // localStorage "last visited" cache (legacy redirect-д ашиглана).
  useEffect(() => {
    if (status === 'ok' && tenantMatch && companyMatch) {
      setAuth('tsetsu.tenantId', tenantId);
      setAuth('tsetsu.companyId', companyId);
      setAuth('tsetsu.tenantName', tenantMatch.Name);
      setAuth('tsetsu.companyName', companyMatch.Name);
    }
  }, [status, tenantId, companyId, tenantMatch, companyMatch]);

  const switchTo = useCallback<TenantScopeValue['switchTo']>(
    (next, opts) => {
      const route = opts?.route ?? 'dashboard';
      // 1. API client-ийн идэвхтэй scope-ыг навигац хийхээс өмнө **синхрон** солих
      //    хэрэгтэй. Шинэ хуудасны child component-уудын `useQuery` effect-үүд
      //    React-ийн effect ordering-аар (children first, parent last) бидний
      //    доорх useEffect-ээс өмнө ажилладаг тул, useEffect дотроос
      //    `setApiActiveScope` дуудах нь race-д орох — шинэ хуудас хуучин
      //    X-Ts-Tenant/Company header-аар query явуулж, хуучин tenant-ийн
      //    мэдээллийг шинэ scope-ийн cache-д бичнэ.
      setApiActiveScope({ tenantId: next.tenantId, companyId: next.companyId });
      // 2. Old scope-ийн query cache-ыг арилгана — өөр tenant/company-ийн data-аар
      //    буруу хэлбэрээр render хийгдэхээс сэргийлнэ.
      queryClient.removeQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k)) return false;
          // Scope query-уудыг үлдээх (`['scope', ...]`) — бусад бүгдийг арилгана.
          if (k[0] === 'scope') return false;
          return true;
        },
      });
      // 3. POS cashier session-ыг цэвэрлэнэ — cashier нь компани-bound тул шинэ
      //    scope-д хуучин кассчны token ашиглагдахаас сэргийлнэ. Терминалын
      //    activation cookie (ts_term)-ыг ХАДГАЛНА — машины binding-ийг PosActivationGate
      //    scope-той тулгаж шалгана.
      clearCashierSession();
      navigate(buildScopedPath(route, next), { replace: opts?.replace ?? false });
    },
    [navigate, queryClient],
  );

  const refresh = useCallback(() => {
    void tenantsQ.refetch();
    void companiesQ.refetch();
  }, [tenantsQ, companiesQ]);

  // Forbidden → user-ыг select-scope-руу. Сонгуулсны дараа scoped URL руу буцна.
  if (status === 'forbidden') {
    return (
      <Navigate
        to={publicRoutes.selectScope}
        replace
        state={{ from: loc.pathname + loc.search, reason: 'scope-forbidden' }}
      />
    );
  }

  const value: TenantScopeValue = {
    tenantId,
    companyId,
    tenantName: tenantMatch?.Name ?? '',
    companyName: companyMatch?.Name ?? '',
    companyBusinessType: companyMatch?.BusinessType ?? null,
    status,
    myRole: toRoleInt(tenantMatch?.MyRole),
    switchTo,
    refresh,
  };

  return (
    <TenantScopeContext.Provider value={value}>
      {children}
    </TenantScopeContext.Provider>
  );
}
